import { Form } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router';

import { confirmClientInvitation, updateClient } from '@/api/client';
import { isAuthTokenResponse, loginUserRequest, updateUser } from '@/api/user';
import { composeSchemaFormWithT } from '@/components/form/fieldDefs/composeForm';
import { buildPersonalInfoGroup } from '@/components/form/fieldDefs/personalInfoFields';
import {
  ACCEPTED_PRIVACY_POLICY_FIELD,
  buildSignUpPrivacyAcceptanceSection,
} from '@/components/form/fieldDefs/signUpPrivacyAcceptance';
import { buildUserPreferenceSection } from '@/components/form/fieldDefs/userPreferenceFields';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { setClientProfileId } from '@/config/axios';
import { CLIENT_ROLE } from '@/constants/roles';
import { DASHBOARD } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { useRefreshSessionUser } from '@/hooks/useRefreshSessionUser';
import { useSupportedLanguages } from '@/hooks/useSupportedLanguages';
import { useUrlState } from '@/hooks/useUrlState';
import { tCatalog } from '@/i18n/tCatalog';
import { useAppDispatch } from '@/store/hooks';
import { login, loginUser } from '@/store/userSlice';
import FormErrorAlert from '@/UI/FormErrorAlert';
import { PageTitle } from '@/UI/typography';
import { storeAccessToken, storeJobId } from '@/utils/localStorage';
import {
  assertPasswordsMatch,
  buildCommonValidationMessages,
  passwordConfirmField,
  passwordField,
  requiredBoolean,
  requiredString,
} from '@/utils/validation/schemaHelpers';

import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { UrlParams } from '@/pages/InvitationConfirm';
import type { InvitedUserDetails } from '@/types/user';

type ClientInvitationFormValues = {
  name?: string;
  title?: string;
  company?: string;
  department?: string;
  email?: string;
  username?: string;
  phone?: string;
  password?: string;
  password_confirm?: string;
  currency?: string;
  language?: string;
  accepted_privacy_policy?: boolean;
};

const toClientUpdatePayload = (
  values: ClientInvitationFormValues,
): Record<string, unknown> => {
  const clientFields = { ...values };
  delete clientFields.password;
  delete clientFields.password_confirm;
  delete clientFields[ACCEPTED_PRIVACY_POLICY_FIELD];
  return clientFields;
};

type ClientInvitationFormProps = {
  user: InvitedUserDetails;
  profileId?: string;
  onInvitationComplete?: () => void;
};

const ClientInvitationForm = ({
  user,
  profileId,
  onInvitationComplete,
}: ClientInvitationFormProps) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<ClientInvitationFormValues>();
  const { uid, token } = useParams() as unknown as UrlParams;
  const { history } = useUrlState();
  const { t } = useTranslation();
  const { showSuccess } = useApiFeedback();
  const refreshSessionUser = useRefreshSessionUser();
  const search = useLocation().search;
  const { languages } = useSupportedLanguages();
  const hydratedUserIdRef = useRef<string | null>(null);
  const composedInviteForm = useMemo(() => {
    const messages = buildCommonValidationMessages(t);
    return composeSchemaFormWithT(
      t,
      [
        buildPersonalInfoGroup({
          t,
          disableDepartment: !!user.department,
          disableFields: true,
          includeHiddenIds: false,
          phoneOptional: false,
        }),
      ],
      {
        scope: 'save',
        extraFields: {
          password: passwordField(messages),
          password_confirm: passwordConfirmField(messages),
          language: requiredString(messages.required),
          currency: requiredString(messages.required),
          [ACCEPTED_PRIVACY_POLICY_FIELD]: requiredBoolean(messages.required),
        },
        extraRefine: (data, ctx) => {
          assertPasswordsMatch(
            data,
            ctx,
            'password',
            'password_confirm',
            messages.passwordMismatch,
          );
        },
      },
    );
  }, [t, user.department]);
  const formSchema = composedInviteForm.formSchema;

  const sections = useMemo<SchemaSection[]>(
    () => [
      ...composedInviteForm.sections,
      {
        key: 'credentials',
        fields: [
          {
            name: 'email',
            type: 'hidden',
          },
          {
            name: 'password',
            type: 'password',
            label: t('PASSWORD_LABEL'),
            help: t('PASSWORD_HINT'),
            colSpan: 12,
          },
          {
            name: 'password_confirm',
            type: 'password',
            label: t('PASSWORD_CONFIRM_LABEL'),
            colSpan: 12,
          },
        ],
      },
      buildUserPreferenceSection({
        t,
        languageOptions: languages.map((lang) => ({
          value: lang.code,
          label: tCatalog('languages', lang.name),
        })),
      }),
      buildSignUpPrivacyAcceptanceSection(),
    ],
    [composedInviteForm.sections, t, languages],
  );

  const checkJob = () => {
    const searchParams = new URLSearchParams(search);
    const jobId = searchParams.get('job_id');

    if (jobId) {
      storeJobId(jobId);
    }
  };

  const inviteMutation = useApiMutation<
    { detail?: string } | undefined,
    ClientInvitationFormValues,
    unknown
  >({
    mutationFn: async (values) => {
      const confirmData = {
        uid,
        token,
        new_password: values.password as string,
      };
      const data = await confirmClientInvitation(confirmData, profileId);

      if (data?.access_token && data.user) {
        const { access_token, user: confirmedUser } = data;
        if (
          user.role === CLIENT_ROLE &&
          (user.available_client_profiles?.length ?? 0) > 0
        ) {
          const selectedProfile = user.available_client_profiles?.find(
            (profile) => profile.id === profileId,
          );
          if (selectedProfile) {
            setClientProfileId(selectedProfile.id);
          } else {
            throw new Error(
              `Profile not found. Invalid profile ID: ${profileId}`,
            );
          }
        }

        storeAccessToken(access_token);
        await updateClient(toClientUpdatePayload(values));
        if (values.language || values.currency) {
          await updateUser({
            default_language: values.language,
            default_currency: values.currency,
          });
        }
        dispatch(login({ user: confirmedUser, access_token }));
        await refreshSessionUser();
        checkJob();
        onInvitationComplete?.();
        history.push(DASHBOARD);
        return undefined;
      } else {
        const credentials: Record<string, unknown> = {
          email: values.email,
          password: values.password,
        };
        if (
          user.role === CLIENT_ROLE &&
          (user.available_client_profiles?.length ?? 0) > 0
        ) {
          const selectedProfile = user.available_client_profiles?.find(
            (profile) => profile.id === profileId,
          );
          if (selectedProfile) {
            credentials.client_profile_id = selectedProfile.id;
            setClientProfileId(selectedProfile.id);
          } else {
            throw new Error(
              `Profile not found. Invalid profile ID: ${profileId}`,
            );
          }
        }

        const loginResponse = await loginUserRequest(credentials);

        if (isAuthTokenResponse(loginResponse) && loginResponse.access_token) {
          storeAccessToken(loginResponse.access_token);
          await updateClient(toClientUpdatePayload(values));
          if (values.language || values.currency) {
            await updateUser({
              default_language: values.language,
              default_currency: values.currency,
            });
          }
          const loginResult = await dispatch(loginUser(credentials));
          if (loginUser.fulfilled.match(loginResult)) {
            await refreshSessionUser();
            checkJob();
            onInvitationComplete?.();
            history.push(DASHBOARD);
          } else {
            throw new Error('Failed to update profile information');
          }
        } else {
          throw new Error('Login failed. Please try again.');
        }
        return { detail: data.detail };
      }
    },
    onSuccess: (result) => {
      if (result?.detail) {
        showSuccess(result.detail);
      }
    },
  });

  const onFinish = (values: ClientInvitationFormValues) => {
    inviteMutation.mutate(values);
  };
  // Route-only onboarding: hydrates once per user identity. Add an `open`/
  // `mountKey` guard before reusing this form inside a modal or drawer.
  useEffect(() => {
    const identityKey = user.id ?? user.email;
    if (hydratedUserIdRef.current === identityKey) return;
    hydratedUserIdRef.current = identityKey;

    form.setFieldsValue({
      name: user.name === 'Username' ? '' : user.name,
      title: user.title,
      company: (user.company as { name?: string })?.name,
      department: user.department,
      email: user.email,
      username: user.email,
      phone: user.phone,
    });
  }, [user, form]);
  return (
    <>
      <PageTitle>{t('PERSONAL_INFO_TITLE')}</PageTitle>
      <FormErrorAlert error={inviteMutation.error} />
      <SchemaForm
        form={form}
        formSchema={formSchema}
        name="invitation"
        scrollToFirstError
        onFinish={onFinish}
        sections={sections}
        footer={{
          actions: [
            {
              label: t('SIGN_UP_TITLE'),
              htmlType: 'submit',
              type: 'primary',
              loading: inviteMutation.loading,
            },
          ],
        }}
      />
    </>
  );
};

export default ClientInvitationForm;
